import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import OrdersAPI, { Order, OrderDress } from "../../../../api/OrdersAPI";
import { PaymentStatus } from "../../../../types/payment-statuses";
import { CancelToken } from "../../../../api/SimpleAPIClient";
import { Filters, useGetOrders } from "../../../../hooks/useGetOrders";
import { useGetShops } from "../../../../hooks/useGetShops";
import { Autocomplete } from "../../../../components/Autocomplete/Autocomplete";
import DropSelect from "../../../../components/DropSelect/DropSelect";
import FilterPill from "../../../../components/FilterPill/FilterPill";
import { Icon } from "../../../../components/Icon/Icon";
import IconButton from "../../../../components/IconButton/IconButton";
import { Modal } from "../../../../components/Modal/Modal";
import OrderBasket from "../../../../components/OrderBasket/OrderBasket";
import { containsFileType } from "../../../../components/OrderElement/OrderElement";
import OrderForm from "../../../../components/OrderForm/OrderForm";
import PrimaryButton from "../../../../components/PrimaryButton/PrimaryButton";
import PrintButton from "../../../../components/PrintButton/PrintButton";
import deleteIcon from "../../../../assets/images/bin.svg";
import hideIcon from "../../../../assets/images/eye.svg";
import showIcon from "../../../../assets/images/eye-slash.svg";
import scaleIcon from "../../../../assets/images/scale.png";
import disabledScaleIcon from "../../../../assets/images/disabled-scale.png";
import funnelIcon from "../../../../assets/images/funnel.svg";
import xMarkIcon from "../../../../assets/images/x-mark.svg";
import ConfirmationPopup from "../../../../components/ConfirmationPopup/ConfirmationPopup";
import { ShopClient } from "../../../../api/ShopClientsAPI";
import { format } from "date-fns";
import { FormType } from "../../../../shared/form.type";
import Subtitle from "../../../../components/Subtitle/Subtitle";
import { PermissionFlags } from "../../../../components/PermissionFlags/PermissionFlags";
import { paymentStatusToAbbreviatedTranslationKey, paymentStatusToPillColor, paymentStatusToTranslationKey } from "../../../../shared/paymentStatusToTranslationKey";
import Pill from "../../../../components/Pill/Pill";
import { BolderText } from "../../../../components/BolderText/BolderText";
import useFeatureFlag from "../../../../hooks/usePermissionFlag";
import lowPriorityIcon from '../../../../assets/images/warning-off.png';
import highPriorityIcon from '../../../../assets/images/warning.png';
import Card from "../../../../components/Card/Card";
import WorkArea from "../../../../components/WorkArea/WorkArea";
import NoDataCTA from "../../../../components/NoDataCTA/NoDataCTA";
import SecondaryButton from "../../../../components/SecondaryButton/SecondaryButton";
import Tab from "../../../../components/Tab/Tab";
import { Link } from "../../../../components/Link/Link";
import Table, { ColumnDefinition } from "../../../../components/Table/Table";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { orderStatusToTranslationKeys } from "../../../../shared/orderStatusToTranslationKey";
import { useSelector } from "react-redux";
import { Settings } from "../../../../store/reducers/AccountSlice";
import CONFIG from "../../../../config/config";
import { useLocation, useNavigate } from "react-router-dom";
import OrderSearchResult from "../../../../components/OrderSearchResult/OrderSearchResult";

const renderOrderDressName = (dress?: OrderDress, dressDescription?: string): React.ReactNode => {
  return (
    <Tooltip content={dress?.name || dressDescription || '-'}>
      <p className="w-32 truncate">{dress?.name || dressDescription}</p>
    </Tooltip>
  );
};

export type OrderColumn = ColumnDefinition<Order>;

function OrdersSection() {

  const { t } = useTranslation();

  const { hasAccess: canPrioritize } = useFeatureFlag(['set-high-priority-orders']);
  const { hasAccess: canEdit } = useFeatureFlag(['update-orders', 'order-story-write']);
  const { hasAccess: canSetPendingMeasurements } = useFeatureFlag(['set-pending-measures-orders']);
  const settings: Settings = useSelector((state: any) => state.settings.value);
  
  const availableOrderStatuses = useMemo(() => {
    if (settings?.isConfigurationLoaded && 
        settings?.allowedOrderStatuses && 
        settings.allowedOrderStatuses.length > 0) {
      return settings.allowedOrderStatuses;
    }
    return CONFIG.ORDERS.STATUS;
  }, [settings?.isConfigurationLoaded, settings?.allowedOrderStatuses]);
  
  const [filters, setFilters] = useState<Filters>({ shopId: '', date: '', hidden: false, external: false, unresolved: false, status: '', paymentStatus: undefined });
  const { isLoadingOrders, orders, updateFn, removeFn, refresh } = useGetOrders(filters);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [showBasket, setShowBasket] = useState(false);
  const [isNewOrder, setIsNewOrder] = useState(false);
  const [order, setOrder] = useState<Order | null>();
  const { shops } = useGetShops();
  const [action, setAction] = useState<FormType>('READ');
  const [temporaryList, setTemporaryList] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [partnerSearchText, setPartnerSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const isFiltering = !!filters.date || !!filters.shopId || !!filters.status || !!filters.paymentStatus;
  const [isInitializing, setIsInitializing] = useState(true);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);

  const clearOrderQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("openForm");
    params.delete("orderId");
    navigate({ search: params.toString() }, { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    const isDataLoaded = !isLoadingOrders && canEdit !== undefined;
    
    if (isDataLoaded && isInitializing) {
      setIsInitializing(false);
    }
  }, [isLoadingOrders, canEdit, isInitializing])

  useEffect(() => {
    return () => {
      setPendingOrderId(null);
    };
  }, [])

  useEffect(() => {
    if (!isInitializing && pendingOrderId) {
      OrdersAPI.getInternalOrderById(pendingOrderId)
        .then(response => {
          const foundOrder = response.data;
          if (foundOrder) {
            if (canEdit) setAction("UPDATE");
            setOrder(foundOrder);
            setFilters(prev => ({ ...prev, external: false }));
          }
          clearOrderQueryParams();
          setPendingOrderId(null);
        })
        .catch(error => {
          console.error("Error fetching internal order by ID:", error);
          clearOrderQueryParams();
          setPendingOrderId(null);
        });
    }
  }, [isInitializing, pendingOrderId, canEdit, navigate, location.search, clearOrderQueryParams])

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openForm = params.get('openForm');
    const orderId = params.get("orderId");

    if (openForm === 'true') {
      setShowBasket(true);
      setIsNewOrder(true);
      clearOrderQueryParams();
    }

    if (orderId) {
      setPendingOrderId(orderId);
    }

  }, [location, navigate, clearOrderQueryParams]);



  const orderColumns: OrderColumn[] = [
    {
      key: 'shopName',
      title: t("orders.details.salon"),
      render: (order) => 
      <Tooltip content={order.shopName!}>
        <p className="w-36 truncate">{order.shopName}</p>
      </Tooltip>,
      rowClass: (or) => 
      `
        ${or.visible && or.status !== 'BLOCKED' ? 'hover:bg-darker-gold-50' : ''} 
        ${or.status === 'BLOCKED' ? 'bg-custom-red' : ''}
        ${!or.visible ? 'bg-darker-gold-100 text-grey-50' : ''} 
        ${or.status === 'SENT' && or.visible ? 'bg-darker-gold-300' : ''}
      `
    },
    {
      key: 'paymentStatus',
      title: t("orders.details.payment_status"),
      render: (order) => (
        <Tooltip content={t(paymentStatusToTranslationKey(order.paymentStatus))}>
          <Pill text={t(paymentStatusToAbbreviatedTranslationKey(order.paymentStatus))} color={paymentStatusToPillColor(order.paymentStatus)}/>
        </Tooltip>
      ),
    },
    {
      key: 'clientName',
      title: t("orders.details.name"),
      render: (order) => <Tooltip content={order.clientName}><p className="w-36 truncate">{order.clientName}</p></Tooltip>,
    },
    {
      key: 'invoiceNumber',
      title: t("orders.details.invoice_number"),
      render: (order) => (
        <Tooltip content={order.invoiceNumber || '-'}>
          <p className="w-15 truncate">{order.invoiceNumber || '-'}</p>
        </Tooltip>
      ),
    },
    {
      key: 'dress',
      title: t("orders.details.description"),
      render: (order) => renderOrderDressName(order.dress, order.dressDescription),
    },
    {
      key: 'deliveryDate',
      title: t("orders.details.delivery_time"),
      render: (order) => <BolderText text={order.deliveryDate} /> ,
    },
    {
      key: 'status',
      title: t("orders.details.status"),
      render: (order) => <Tooltip content={t(orderStatusToTranslationKeys(order.status))}><p className="w-12 sm:w-20 truncate">{ t(orderStatusToTranslationKeys(order.status)) }</p></Tooltip>,
    },
    {
      key: 'attachmentUrls',
      title: t("orders.details.files"),
      render: (order) => (
        <div className="flex space-x-3">
          { order.notes? <span className="w-3">📝</span> : null }
          { containsFileType(order.attachmentUrls || [], CONFIG.VIDEO_TYPES)? <span className="w-3">📹</span> : null }
          { containsFileType(order.attachmentUrls || [], CONFIG.TEXT_TYPES.concat(CONFIG.IMAGE_TYPES))? <span className="w-3">📁</span> : null }
        </div>
      ),
    },
  ];

  const handleSelection = (itemOrder: Order) => {
  
    setTemporaryList(prevList => {
      if (prevList.some(order => order?.id === itemOrder.id)) {
        return prevList.filter(order => order?.id !== itemOrder.id);
      } else {
        return [...prevList, itemOrder];
      }
    });
  };

  const actionButton = (action: FormType) => {
    if (isLoadingOrders) {
      return <span className="flex justify-center"><PrimaryButton text="..." isLoading /></span>
    }
    switch (action) {
      case 'UPDATE':
        return <span className="flex justify-center"><PrimaryButton text={t("admin.collections.items.form.update")!} fn={processUpdateOrder} /></span>;
      default:
        return;
    }
  };

  const search = (input: string, resultsSize: number, setResultsFn: any) => {
    setSearchIsLoading(true);
    OrdersAPI.search(input).then((resp) => {
      const raw = resp.data;
      const respRaw = raw.slice(0, resultsSize);
      setSearchIsLoading(false);
      setResultsFn(respRaw);
    });
    return CancelToken.source().cancel("Cancelling...");
  };

  const processUpdateOrder = () => {
    processItem(updateFn, order!);
  }

  async function processItem(actionFn: (item: Order) => Promise<void>, item: Order
  ) {
    await actionFn(item);
    setOrder(null);
    setAction('READ');
    refresh();
  }

  const view = (or: Order) => {
    setOrder(or);
    if (canEdit) {
      setAction('UPDATE');
    } else {
      setAction('READ');
    }
  }

  const changePriority = async (or: Order) => {
    const existing = or.prioritized;
    const reverse = !existing;
    or.prioritized = reverse;
    processItem(updateFn, or);
  };

  const setPendingMeasurements = async (or: Order) => {
    const existing = or.pendingMeasures;
    const reverse = !existing;
    or.pendingMeasures = reverse;
    processItem(updateFn, or);
  };

  const hide = async (or: Order) => {
    or.visible = !or.visible;
    processItem(updateFn, or);
  };

  const setShopFilter = (s: ShopClient) => {
    setShop(s);
  }

  const setShop = (s: ShopClient) => {
    setFilters(prev => { 
      if (prev.shopId === String(s.id!)) {
        let copy = Object.assign({}, prev); 
        return {...copy, shopId: ''};
      } else {
        let copy = Object.assign({}, prev); 
        return {...copy, shopId: String(s.id!)};
      }
    })
  }

  const setHidden = (status: boolean) => {
    setFilters(prev => { 
        let copy = Object.assign({}, prev); 
        return {...copy, hidden: status, external: false, status: ''};
    });
  }

  const setDate = (e: Date) => { 
    if (e) {
      const tempDate = new Date(e);
      const formatTempDate = format(tempDate!, "yyyy-MM-dd");
      setFilters(prev => { return {...prev, date: formatTempDate}})
    } else {
      setFilters(prev => { return {...prev, date: ''}});
    }
  }

  const setStatus = (status: string) => {
    setFilters(prev => { 
      if (prev.status === status) {
        return {...prev, status: ''};
      } else {
        return {...prev, status: status};
      }
    });
  }

  const setPaymentStatus = (paymentStatus: PaymentStatus) => {
    setFilters(prev => { 
      if (prev.paymentStatus === paymentStatus) {
        return {...prev, paymentStatus: undefined};
      } else {
        return {...prev, paymentStatus: paymentStatus};
      }
    });
  }

  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(partnerSearchText.toLowerCase())
  );

  const isDifferentMonth = (date1: Date, date2: Date) => {
    return date1.getMonth() !== date2.getMonth() || date1.getFullYear() !== date2.getFullYear();
  };

  const createNewBatchOrder = () => {
    if (showBasket) {
      setShowBasket(prev => !prev)
    } else {
      setShowBasket(prev => !prev);
      setIsNewOrder(true);
    }
  }

  const closeModal = () => {
    setOrder(null);
    setAction('READ');
  }

  const renderActions = (order: Order, index: number): React.ReactNode => {
    return (
      <span className="flex">
        <IconButton 
          actionName="Pending Measurements"
          onClick={() => { if (canSetPendingMeasurements) setPendingMeasurements(order); }}>
          <Tooltip content={t("orders.actions.measures")}>
            <Icon url={order?.pendingMeasures ? scaleIcon : disabledScaleIcon} cssStyles="w-5"/>
          </Tooltip>
        </IconButton>
        <IconButton 
          actionName="Set High Priority"
          onClick={() => { if (canPrioritize) changePriority(order); }}>
          <Tooltip content={t("orders.actions.priority")}>
            <Icon url={order?.prioritized ? highPriorityIcon : lowPriorityIcon} cssStyles="w-5"/>
          </Tooltip>
        </IconButton>
        <PermissionFlags requiredFlags={['hide-orders']}>
          <IconButton 
            actionName="Hide/Show Order"
            onClick={() => hide(order)}>
            <Tooltip content={order.visible ? t("orders.actions.hide") : t("orders.actions.show")}>
              <Icon url={order.visible ? hideIcon : showIcon } cssStyles="w-5"/>
            </Tooltip>
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['remove-orders']}>
          <IconButton
            actionName="Delete Order"
            >
            <ConfirmationPopup onConfirm={() => removeFn(order)} confirmText={"global.confirm"} title={`${t("global.remove")}`}>
              <Icon url={deleteIcon} cssStyles="w-5"/>
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </span>
    );
  };

  return (
      <div className="mt-5 space-y-5">
      <Card>
        <>
          <div className="flex-col">
            <div className="md:flex md:flex-row md:items-center md:space-x-3">
              <div className="flex items-center space-x-3 mb-2 md:mb-0">
                <DropSelect compact onSelect={(e: any) => setDate(e.target.value)} value={filters?.date?.toString()}/>
                <span className="md:hidden">
                  <SecondaryButton 
                    text={<span className="inline-flex items-center">{t("filters.button") || "Filters"}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                    fn={() => setShowFilters(!showFilters)}
                    icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                  />
                </span>
              </div>
              <Autocomplete
                resultsSize={15}
                searchFn={search}
                onSelect={() => null}
                placeholder={"orders.search.placeholder"} 
                isLoading={searchIsLoading}
                errorMessage={"global.empty_results"}
                toElementFn={(or: Order) => 
                  <OrderSearchResult
                    key={or.id}
                    order={or}
                    onView={view}
                    onSetPendingMeasurements={canSetPendingMeasurements ? setPendingMeasurements : undefined}
                    onChangePriority={canPrioritize ? changePriority : undefined}
                    onHide={hide}
                    canSetPendingMeasurements={canSetPendingMeasurements}
                    canPrioritize={canPrioritize}
                  />
                }
              />
              <div className="hidden md:flex">
                <SecondaryButton 
                  text={<span className="inline-flex items-center">{t("filters.button") || "Filters"}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                  fn={() => setShowFilters(!showFilters)}
                  icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                />
              </div>
            </div>
          </div>
          {showFilters && (
            <>
              <div className="space-y-2 mt-2">
                {/* Partner Shops Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t("orders.filters.shops.title") || "Partner Shops"}</h3>
                  <div className="w-full max-w-full">
                    <label>
                      <span className="autocompleteInputWrapper formValue autocomplete">
                        <input
                          className="p-3 w-full border-stone-200 border rounded focus:outline-none"
                          type="text"
                          placeholder={t("orders.filters.shops.search_placeholder")!}
                          value={partnerSearchText}
                          onChange={(e) => setPartnerSearchText(e.target.value)}
                          data-dd-action-name={'Partner Search'}
                        />
                      </span>
                    </label>
                  </div>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {
                      filteredShops.length > 0 ? filteredShops.map((s, i) => <FilterPill isActive={filters?.shopId === String(s.id)} key={i} text={s.name} fn={() => setShopFilter(s)} />)
                      : partnerSearchText ? (
                        <p>{t("orders.filters.shops.no_results")}</p>
                      ) : (
                        <>
                          <p>{t("orders.filters.shops.empty")}.</p>&nbsp;
                          <Link url="/partners" text={t("orders.filters.shops.empty.cta")}/>
                        </>
                      )
                    }
                  </div>
                </div>

                {/* Order Status Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t("orders.filters.status.title") || "Order Status"}</h3>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {availableOrderStatuses
                      .map(status => ({
                        status,
                        translatedText: t(orderStatusToTranslationKeys(status))
                      }))
                      .sort((a, b) => a.translatedText.localeCompare(b.translatedText))
                      .map(({ status, translatedText }, i) => (
                        <FilterPill 
                          key={i} 
                          text={translatedText} 
                          isActive={filters?.status === status}
                          fn={() => setStatus(status)} 
                        />
                      ))}
                  </div>
                </div>

                {/* Payment Status Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t("orders.filters.payment_status.title") || "Payment Status"}</h3>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {CONFIG.ORDERS.PAYMENT_STATUS.map((paymentStatus, i) => (
                      <FilterPill 
                        key={i} 
                        text={t(paymentStatusToTranslationKey(paymentStatus))} 
                        isActive={filters?.paymentStatus === paymentStatus}
                        fn={() => setPaymentStatus(paymentStatus)} 
                      />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex-col md:flex md:flex-row justify-between md:justify-start items-center space-x-4 mt-3">
            <PermissionFlags requiredFlags={['order-add']}>
              <PrimaryButton actionName="Open/Close Order Basket" text={showBasket ? t("orders.orders_basket.close_form")! : t("orders.orders_basket.add_form")!} fn={() => createNewBatchOrder()}/>
            </PermissionFlags>
            <PermissionFlags requiredFlags={['print-orders']}>
              <PrintButton orders={orders}/>
            </PermissionFlags>
            { (!!filters.date || !!filters.shopId || !!filters.status || !!filters.paymentStatus) && (
              <SecondaryButton 
                actionName="Show All Orders" 
                text={t("filters.reset")!} 
                fn={() => setFilters({ shopId: '', date: '', hidden: filters.hidden, external: false, status: '', paymentStatus: undefined })}
                icon={
                  <span className="relative inline-block w-4 h-4">
                    <Icon url={funnelIcon} cssStyles="w-4 h-4" />
                    <span className="absolute -top-1 -right-1">
                      <Icon url={xMarkIcon} cssStyles="w-3 h-3" />
                    </span>
                  </span>
                }
                compact
              />
            ) }
          </div>
        </>
      </Card>
        { showBasket && <><OrderBasket openFormOnLoad={isNewOrder} fn={refresh} /></> }
        <Card>
          <>
            <div className="flex">
              <Tab fn={() => setHidden(false)} label={t("orders.listing.visible")} isActive={!filters.hidden}/>
              <Tab fn={() => {setHidden(true)}} label={t("orders.listing.hidden")} isActive={filters.hidden}/>
            </div>
            <>
              <div className="container">
                <Table<Order>
                  isLoading={isLoadingOrders}
                  data={orders}
                  columns={orderColumns}
                  onRowClick={(or) => { setOrder(or); setAction(canEdit ? 'UPDATE' : 'READ'); }}
                  compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                  emptyListComponent={
                    <NoDataCTA
                      headline={t("orders.empty.cta.title")}
                      subtext={t("orders.empty.cta.body")}
                      primaryActionText={t("orders.orders_basket.add_form")}
                      onPrimaryAction={() => createNewBatchOrder()}
                    />
                  }
                isSelectable
                onSelect={handleSelection}
                renderActions={renderActions}
                dividerCondition={(current, next) => isDifferentMonth(new Date(current.deliveryDate), new Date(next.deliveryDate))}
                renderDivider={(current) => (
                  <div className="mt-10 mb-4">
                    <Subtitle text={`${t(`global.${format(new Date(current.deliveryDate), "MMMM").toLowerCase()}`)} ${format(new Date(current.deliveryDate), "yyyy")}`} />
                  </div>
                )}
                selectedItems={temporaryList}
                />
              </div>
            </>
          </>
        </Card>
        {temporaryList.length > 0 && 
          <WorkArea 
            items={temporaryList}
            callbackFn={setTemporaryList}
            renderItem={(item: any) => <span className="flex space-x-3"><BolderText text={item?.shopName}/><span>{item?.clientName}</span></span>}
            onItemToggle={handleSelection}
            actions={
              <div className="flex space-x-3">
                <PermissionFlags requiredFlags={['print-orders']}>
                  <PrintButton orders={temporaryList}/>
                </PermissionFlags>
              </div>
            }
          /> 
        }
        { order && 
          <Modal modalTitle={order.clientName} canClose onClose={closeModal}>
            <OrderForm
              order={order}
              isDisabled={action === 'READ' || isLoadingOrders}
              onOrderChange={setOrder}
              actionButton={actionButton(action)}
            />
          </Modal>
        }
      </div>
  );
}

export default OrdersSection;

